import axios from 'axios'

/**
 * Backend endpoints report validation failures using a consistent `ErrorBody { code, description }` shape
 * (see `de.notch.errors.ErrorBody`). This extracts a user-facing message from that shape so callers can show
 * the server's actual validation message instead of a generic "something went wrong" fallback.
 */
export function extractApiErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data
  if (data == null || typeof data !== 'object') {
    return null
  }

  const body = data as { description?: unknown; message?: unknown }
  if (typeof body.description === 'string' && body.description.trim()) {
    return body.description
  }
  if (typeof body.message === 'string' && body.message.trim()) {
    return body.message
  }
  return null
}

/** Machine-readable `ErrorBody.code` when present (e.g. `document-signed`). */
export function extractApiErrorCode(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data
  if (data == null || typeof data !== 'object') {
    return null
  }
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' && code.trim() ? code : null
}

export function getApiErrorStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | undefined)?.response?.status
  return typeof status === 'number' ? status : undefined
}

/** True only for Axios requests that did not receive a response (offline, DNS failure or timeout). */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response && !axios.isCancel(error)
}
